import React, { useContext, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../context/AuthContext";
import { ThemeContext } from "./theme";
import { UserPlus, RefreshCw, CheckCircle, XCircle, AlertCircle, Shield } from "lucide-react";

const AdminAccountCreation = () => {
    const { theme } = useContext(ThemeContext);
    const BASE_URL = process.env.REACT_APP_BASE_URL;

    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    const [form, setForm] = useState({
        sr: "",
        email: "",
        clientName: "",
        infinityCode: "",
        phoneNumber: "",
        location: "",
        password: "",
    });

    const inputStyle = useMemo(
        () => ({
            width: "100%",
            padding: "10px 12px",
            borderRadius: "8px",
            border: theme === "light" ? "1px solid #cbd5e1" : "1px solid rgba(255, 255, 255, 0.2)",
            background: theme === "light" ? "#ffffff" : "rgba(255, 255, 255, 0.05)",
            color: theme === "light" ? "#0f172a" : "#ffffff",
            fontSize: "14px",
            outline: "none",
        }),
        [theme]
    );

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    };

    const fetchCompanies = async () => {
        try {
            setLoadingCompanies(true);
            const response = await apiFetch(`${BASE_URL}/api/configurations/companies`, { method: "GET" });
            const data = await response.json();
            const list = data?.data || [];
            setCompanies(list);
            if (!form.clientName && list.length > 0) {
                setForm((prev) => ({ ...prev, clientName: list[0].clientName }));
            }
        } catch (error) {
            console.error("Failed to load companies:", error);
            showMessage("error", "Failed to load companies");
        } finally {
            setLoadingCompanies(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const onChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const validate = () => {
        if (!form.sr.trim()) {
            showMessage("error", "Admin name is required");
            return false;
        }
        if (!form.email.trim()) {
            showMessage("error", "Email is required");
            return false;
        }
        if (!form.clientName.trim()) {
            showMessage("error", "Company is required");
            return false;
        }
        if (!form.password.trim()) {
            showMessage("error", "Password is required");
            return false;
        }
        if (form.password.trim().length < 4) {
            showMessage("error", "Password must be at least 4 characters");
            return false;
        }
        return true;
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setSaving(true);

            const payload = {
                sr: form.sr.trim(),
                email: form.email.trim(),
                clientName: form.clientName.trim(),
                employeeState: "Active",
                password: form.password,
                loginCount: 2,
            };

            if (form.infinityCode.trim()) payload.infinityCode = form.infinityCode.trim();
            if (form.phoneNumber.trim()) payload.phoneNumber = form.phoneNumber.trim();
            if (form.location.trim()) payload.location = form.location.trim();

            const createResponse = await apiFetch(`${BASE_URL}/employees`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!createResponse.ok) {
                const errorText = await createResponse.text();
                throw new Error(errorText || "Failed to create employee profile");
            }

            const createJson = await createResponse.json();
            const createdInfinityCode =
                createJson?.employee?.infinityCode || createJson?.data?.infinityCode || payload.infinityCode;

            if (!createdInfinityCode) {
                throw new Error("Employee created but Infinity Code is missing");
            }

            const promoteResponse = await apiFetch(`${BASE_URL}/employees/make-admin`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    infinityCodes: [createdInfinityCode],
                    clientName: form.clientName.trim(),
                }),
            });

            if (!promoteResponse.ok) {
                const errorText = await promoteResponse.text();
                throw new Error(errorText || "Employee created but failed to grant admin role");
            }

            showMessage("success", `Admin account created for ${form.email.trim()}`);
            setForm({
                sr: "",
                email: "",
                clientName: form.clientName,
                infinityCode: "",
                phoneNumber: "",
                location: "",
                password: "",
            });
        } catch (error) {
            console.error("Failed to create admin account:", error);
            showMessage("error", error.message || "Failed to create admin account");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ marginTop: "-30px" }}>
            <div
                style={{
                    background: theme === "light" ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.08)",
                    backdropFilter: "blur(20px)",
                    border: theme === "light" ? "1px solid rgba(0, 0, 0, 0.1)" : "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "16px",
                    padding: "24px",
                    marginLeft: 20,
                }}
            >
                <h2
                    style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: theme === "light" ? "#0f172a" : "#ffffff",
                        margin: "0 0 8px 0",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    <Shield size={24} />
                    Admin Account Creation
                </h2>

                <p
                    style={{
                        color: theme === "light" ? "#64748b" : "rgba(255,255,255,0.7)",
                        margin: "0 0 20px 0",
                        fontSize: "14px",
                    }}
                >
                    Create a new employee account and assign admin role immediately.
                </p>

                {message.text && (
                    <div
                        style={{
                            padding: "12px 16px",
                            borderRadius: "8px",
                            marginBottom: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            background:
                                message.type === "success"
                                    ? "rgba(16, 185, 129, 0.1)"
                                    : message.type === "error"
                                        ? "rgba(239, 68, 68, 0.1)"
                                        : "rgba(251, 191, 36, 0.1)",
                            border:
                                message.type === "success"
                                    ? "1px solid #10b981"
                                    : message.type === "error"
                                        ? "1px solid #ef4444"
                                        : "1px solid #fbbf24",
                            color:
                                message.type === "success" ? "#10b981" : message.type === "error" ? "#ef4444" : "#fbbf24",
                        }}
                    >
                        {message.type === "success" ? <CheckCircle size={18} /> : message.type === "error" ? <XCircle size={18} /> : <AlertCircle size={18} />}
                        {message.text}
                    </div>
                )}

                <form onSubmit={handleCreateAdmin}>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: "14px",
                        }}
                    >
                        <div>
                            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 600 }}>Admin Name</label>
                            <input type="text" value={form.sr} onChange={(e) => onChange("sr", e.target.value)} style={inputStyle} placeholder="Full name" />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 600 }}>Email</label>
                            <input type="email" value={form.email} onChange={(e) => onChange("email", e.target.value)} style={inputStyle} placeholder="admin@company.com" />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 600 }}>Company</label>
                            <select
                                value={form.clientName}
                                onChange={(e) => onChange("clientName", e.target.value)}
                                style={inputStyle}
                                disabled={loadingCompanies}
                            >
                                {!form.clientName && <option value="">Select company</option>}
                                {companies.map((company) => (
                                    <option key={company.clientName} value={company.clientName}>
                                        {company.clientName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 600 }}>Password</label>
                            <input
                                type="password"
                                value={form.password}
                                onChange={(e) => onChange("password", e.target.value)}
                                style={inputStyle}
                                placeholder="Enter account password"
                            />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 600 }}>Infinity Code (optional)</label>
                            <input type="text" value={form.infinityCode} onChange={(e) => onChange("infinityCode", e.target.value)} style={inputStyle} placeholder="Leave empty to auto-generate" />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 600 }}>Phone (optional)</label>
                            <input type="text" value={form.phoneNumber} onChange={(e) => onChange("phoneNumber", e.target.value)} style={inputStyle} placeholder="e.g. 010xxxxxxx" />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 600 }}>Location (optional)</label>
                            <input type="text" value={form.location} onChange={(e) => onChange("location", e.target.value)} style={inputStyle} placeholder="Office/Branch" />
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: "16px",
                            padding: "10px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            background: theme === "light" ? "rgba(46, 124, 186, 0.08)" : "rgba(46, 124, 186, 0.18)",
                            color: theme === "light" ? "#1f2937" : "rgba(255,255,255,0.85)",
                        }}
                    >
                        Password will be securely hashed with bcrypt before storing in the database.
                    </div>

                    <button
                        type="submit"
                        disabled={saving || loadingCompanies}
                        style={{
                            marginTop: "16px",
                            background: "linear-gradient(135deg, #2e7cba, #6366f1)",
                            color: "white",
                            border: "none",
                            borderRadius: "10px",
                            padding: "12px 18px",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: saving || loadingCompanies ? "not-allowed" : "pointer",
                            opacity: saving || loadingCompanies ? 0.7 : 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        {saving ? <RefreshCw size={18} style={{ animation: "spin 1s linear infinite" }} /> : <UserPlus size={18} />}
                        {saving ? "Creating Admin..." : "Create Admin Account"}
                    </button>
                </form>
            </div>

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default AdminAccountCreation;